import { ConfirmSmall } from '@wix/wix-ui-icons-common';
import { embeddedScriptAppStatus } from '../../manage-installed-apps-page';
import { Badge, Tooltip } from '@wix/design-system';
import { canUsePremiumApp, isBundleApp, isBundleAppAndInstalled, isBundleAppAndNotInstalled, isBundleAppFromVoucher, isEmbeddedScriptApp, isNewVersionAvailable, isPendingApp, isSetupIncomplete, } from '../../..';
import React, { useState, useRef } from 'react';
import { useExperiments } from '@wix/wix-experiments-react';
import { useTranslation } from '../../../hooks/use-translation';
const getLabel = (app, appEmbeddedScriptAppsStatus, t, siteIsPremium, experiments, siteIsBusinessFirstSite, bundleApps, benefitApps) => {
    const appId = app?.appId;
    if (isBundleAppAndInstalled(appId, bundleApps) &&
        isBundleAppFromVoucher(appId, bundleApps)) {
        return t('manage-apps.status.badge.bundle-voucher-installed');
    }
    if (isBundleApp(appId, bundleApps, benefitApps, experiments)) {
        return t('manage-apps.status.badge.bundle');
    }
    if (!canUsePremiumApp(app, siteIsPremium)) {
        return t('manage-apps.status.badge.premium');
    }
    if (isNewVersionAvailable(app)) {
        return t('manage-apps.status.badge.update-version');
    }
    if (isPendingApp(app)) {
        if (siteIsBusinessFirstSite) {
            return t('manage-apps.status.badge.site-pending');
        }
        return t('manage-apps.status.badge.Pending');
    }
    if (isSetupIncomplete(app)) {
        return t('manage-apps.status.badge.incomplete-setup');
    }
    if (isEmbeddedScriptApp(app)) {
        switch (appEmbeddedScriptAppsStatus) {
            case embeddedScriptAppStatus.ENABLED:
                return t('manage-apps.status.badge.running');
            default:
                return t('manage-apps.status.badge.Paused');
        }
    }
};
const getTheme = (app, appEmbeddedScriptAppsStatus, siteIsPremium, experiments, bundleApps, benefitApps) => {
    const appId = app?.appId;
    if (isBundleApp(appId, bundleApps, benefitApps, experiments)) {
        return {
            skin: 'success',
            type: 'solid',
            size: 'tiny',
            uppercase: false,
        };
    }
    if (!canUsePremiumApp(app, siteIsPremium)) {
        return {
            skin: 'neutralDanger',
            type: 'solid',
            size: 'tiny',
            uppercase: false,
        };
    }
    if (isPendingApp(app) ||
        isNewVersionAvailable(app) ||
        isSetupIncomplete(app)) {
        return {
            skin: 'neutralLight',
            type: 'outlined',
            size: 'tiny',
            uppercase: false,
        };
    }
    if (isEmbeddedScriptApp(app)) {
        switch (appEmbeddedScriptAppsStatus) {
            case embeddedScriptAppStatus.ENABLED:
                return {
                    skin: 'success',
                    type: 'outlined',
                    size: 'tiny',
                    uppercase: false,
                    prefixIcon: React.createElement(ConfirmSmall, null),
                };
            default:
                return {
                    skin: 'neutralDanger',
                    type: 'outlined',
                    size: 'tiny',
                    uppercase: false,
                };
        }
    }
};
const shouldShowBadge = (app, appEmbeddedScriptAppsStatus, siteIsPremium, experiments, bundleApps, benefitApps) => {
    const appId = app?.appId;
    const isBenefitsExperiment = experiments.enabled('specs.app-market.MoveBundlesToBenefits');
    if (!canUsePremiumApp(app, siteIsPremium)) {
        return true;
    }
    if (isBenefitsExperiment) {
        if (isPendingApp(app)) {
            return true;
        }
    }
    else {
        if (isPendingApp(app) || isBundleApp(appId, bundleApps, benefitApps, experiments)) {
            return true;
        }
    }
    if (isNewVersionAvailable(app)) {
        return true;
    }
    if (isSetupIncomplete(app)) {
        return true;
    }
    if (isEmbeddedScriptApp(app)) {
        return (appEmbeddedScriptAppsStatus &&
            appEmbeddedScriptAppsStatus === embeddedScriptAppStatus.DISABLED);
    }
    return false;
};
const shouldShowTooltip = (app, appEmbeddedScriptAppsStatus, siteIsPremium, experiments, bundleApps, benefitApps) => {
    const appId = app?.appId;
    if (!canUsePremiumApp(app, siteIsPremium)) {
        return true;
    }
    if (isPendingApp(app) || isBundleApp(appId, bundleApps, benefitApps, experiments)) {
        return true;
    }
    if (isNewVersionAvailable(app)) {
        return true;
    }
    if (isSetupIncomplete(app)) {
        return true;
    }
    return false;
};
const getTooltipContent = (app, appEmbeddedScriptAppsStatus, t, siteIsPremium, experiments, siteIsBusinessFirstSite, bundleApps) => {
    const appId = app?.appId;
    const appName = app?.appInfo?.name;
    if (isBundleAppAndNotInstalled(appId, bundleApps) &&
        isBundleAppFromVoucher(appId, bundleApps)) {
        return t('manage-apps.status.badge.tooltip-bundle-voucher-not-installed', {
            appName,
        });
    }
    if (isBundleAppAndNotInstalled(appId, bundleApps)) {
        return t('manage-apps.status.badge.tooltip-bundle-not-installed', {
            appName,
        });
    }
    if (isBundleAppAndInstalled(appId, bundleApps) &&
        isBundleAppFromVoucher(appId, bundleApps)) {
        return t('manage-apps.status.badge.tooltip-bundle-installed-voucher', {
            appName,
        });
    }
    if (isBundleAppAndInstalled(appId, bundleApps)) {
        return t('manage-apps.status.badge.tooltip-bundle-installed', {
            appName,
        });
    }
    if (!canUsePremiumApp(app, siteIsPremium)) {
        return t('manage-apps.status.badge.tooltip-premium');
    }
    if (isNewVersionAvailable(app)) {
        return t('manage-apps.status.badge.tooltip-update-version');
    }
    if (isPendingApp(app)) {
        if (siteIsBusinessFirstSite) {
            return t('manage-apps.status.badge.tooltip-site-pending');
        }
        return t('manage-apps.status.badge.tooltip-pending');
    }
    if (isSetupIncomplete(app)) {
        return t('manage-apps.status.badge.tooltip-complete-setup');
    }
    if (isEmbeddedScriptApp(app)) {
        return t('manage-apps.status.badge.tooltip-missing', {
            appName,
        });
    }
};
function AppBadgeBase({ app, appEmbeddedScriptAppsStatus, siteIsPremium, siteIsBusinessFirstSite, bundleApps, benefitApps }) {
    const t = useTranslation();
    const { experiments } = useExperiments();
    return (React.createElement(Badge, { ...getTheme(app, appEmbeddedScriptAppsStatus, siteIsPremium, experiments, bundleApps, benefitApps), dataHook: "app-section-app-badge" }, getLabel(app, appEmbeddedScriptAppsStatus, t, siteIsPremium, experiments, siteIsBusinessFirstSite, bundleApps, benefitApps)));
}
export function AppBadge({ app, appEmbeddedScriptAppsStatus, siteIsPremium, siteIsBusinessFirstSite, bundleApps, benefitApps }) {
    const { experiments } = useExperiments();
    const t = useTranslation();
    const [show, setShow] = useState(shouldShowBadge(app, appEmbeddedScriptAppsStatus, siteIsPremium, experiments, bundleApps, benefitApps));
    const updateTimer = useRef(null);
    const firstRender = useRef(true);
    function setUpdate() {
        setShow(!firstRender.current);
        updateTimer.current = setTimeout(() => {
            setShow(false);
            updateTimer.current = null;
        }, 2000);
    }
    React.useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (!updateTimer.current &&
            appEmbeddedScriptAppsStatus === embeddedScriptAppStatus.ENABLED) {
            setUpdate();
        }
        else {
            setShow(true);
        }
    }, [appEmbeddedScriptAppsStatus]);
    React.useEffect(() => {
        return () => {
            if (updateTimer.current) {
                clearTimeout(updateTimer.current);
            }
        };
    }, []);
    return show ? (shouldShowTooltip(app, appEmbeddedScriptAppsStatus, siteIsPremium, experiments, bundleApps, benefitApps) ? (React.createElement(Tooltip, { appendTo: "scrollParent", maxWidth: 224, content: getTooltipContent(app, appEmbeddedScriptAppsStatus, t, siteIsPremium, experiments, siteIsBusinessFirstSite, bundleApps) },
        React.createElement(AppBadgeBase, { app: app, appEmbeddedScriptAppsStatus: appEmbeddedScriptAppsStatus, siteIsPremium: siteIsPremium, siteIsBusinessFirstSite: siteIsBusinessFirstSite, bundleApps: bundleApps, benefitApps: benefitApps }))) : (React.createElement(AppBadgeBase, { app: app, appEmbeddedScriptAppsStatus: appEmbeddedScriptAppsStatus, siteIsPremium: siteIsPremium, siteIsBusinessFirstSite: siteIsBusinessFirstSite, bundleApps: bundleApps, benefitApps: benefitApps }))) : (React.createElement("div", null));
}
//# sourceMappingURL=app-badge.js.map